import { useCallback, useEffect, useRef, useState } from 'react';
import { logTaskFocusSession } from '../services/projectService';
import { runOperation, showOperationFeedback } from '../utils/operationFeedback';
import {
  advanceFocusTimer,
  formatFocusCountdown,
  focusPhaseLabel,
  focusSessionFromTimer,
  MIN_LOGGED_FOCUS_SECONDS,
  remainingSeconds,
  startFocusTimer,
  type ActiveFocusTimer,
  type FocusTimerMode,
} from '../utils/taskFocusTimer';

export type TaskFocusTimerView = {
  taskId: number;
  mode: FocusTimerMode;
  phase: 'focus' | 'break';
  phaseLabel: string;
  remainingLabel: string;
  remainingSeconds: number;
};

export type TaskFocusTimerOptions = {
  /** Project of the timed tasks. Without it, sessions are not logged. */
  projectId?: number;
  /** Called once a session is saved, with the task's new total time spent. */
  onSessionLogged?: (taskId: number, timeSpentMinutes: number) => void;
};

export function useTaskFocusTimer(options: TaskFocusTimerOptions = {}) {
  const [timer, setTimer] = useState<ActiveFocusTimer | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const timerRef = useRef(timer);
  timerRef.current = timer;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const mountedRef = useRef(true);

  /**
   * Saves the focus time of a session that just ended as a time entry on its
   * task. The API takes the employee from the signed-in session.
   */
  const logSession = useCallback(
    (ended: ActiveFocusTimer, endedAtMs: number, notifyTooShort: boolean) => {
      const { projectId } = optionsRef.current;
      if (projectId == null) return;
      const session = focusSessionFromTimer(ended, endedAtMs);
      if (session.durationSeconds < MIN_LOGGED_FOCUS_SECONDS) {
        if (notifyTooShort) {
          showOperationFeedback(
            'add',
            'failure',
            'Focus sessions shorter than 1 minute are not logged.',
          );
        }
        return;
      }
      runOperation('add', () =>
        logTaskFocusSession(projectId, session.taskId, {
          focusType: session.focusType,
          startedAt: session.startedAt.toISOString(),
          endedAt: session.endedAt.toISOString(),
        }),
      )
        .then(result => {
          if (mountedRef.current) {
            optionsRef.current.onSessionLogged?.(session.taskId, result.timeSpentMinutes);
          }
        })
        // runOperation already showed the failure popup.
        .catch(() => undefined);
    },
    [],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Leaving the screen ends the session; keep the focus time it earned.
      const current = timerRef.current;
      timerRef.current = null;
      if (current) logSession(current, Date.now(), false);
    };
  }, [logSession]);

  useEffect(() => {
    if (!timer) return undefined;
    const id = setInterval(() => {
      const current = timerRef.current;
      if (!current) return;
      const tick = Date.now();
      const next = advanceFocusTimer(current, tick);
      if (next !== current) {
        setTimer(next);
        timerRef.current = next;
        if (next == null) logSession(current, tick, false);
      }
      setNowMs(tick);
    }, 250);
    return () => clearInterval(id);
  }, [timer?.taskId, timer?.mode, timer?.phase, timer?.endsAt, logSession]);

  const start = useCallback(
    (taskId: number, mode: FocusTimerMode) => {
      const now = Date.now();
      const previous = timerRef.current;
      if (previous) logSession(previous, now, false);
      const next = startFocusTimer(taskId, mode, now);
      timerRef.current = next;
      setTimer(next);
      setNowMs(now);
    },
    [logSession],
  );

  /** Stop / ✕: ends the session and logs its focus time on the task. */
  const stop = useCallback(() => {
    const current = timerRef.current;
    timerRef.current = null;
    setTimer(null);
    if (current) logSession(current, Date.now(), true);
  }, [logSession]);

  let active: TaskFocusTimerView | null = null;
  if (timer) {
    const remaining = remainingSeconds(timer.endsAt, nowMs);
    active = {
      taskId: timer.taskId,
      mode: timer.mode,
      phase: timer.phase,
      phaseLabel: focusPhaseLabel(timer.phase),
      remainingLabel: formatFocusCountdown(remaining),
      remainingSeconds: remaining,
    };
  }

  return { active, start, stop };
}
